// ────────────────────────────────────────────────────────────────────
// FieldSchematicPanel — opens when an Atlas cluster of CO-LOCATED sites is
// clicked. Those sites share one real coordinate, so they can't be separated on
// the map by zoom. This panel lists them as full plant cards; every number shown
// is real, RLS-scoped. Clicking a card opens that site.
// ────────────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, Anchor, Factory } from "lucide-react";
import type { Plant } from "@/lib/types";
import PlantCard from "@/components/PlantCard";

interface Props {
  field: string;
  plants: Plant[];
  // Co-located OFFSHORE platforms (Valeura) use a "field / platforms" wording;
  // ONSHORE co-located units use "site / units". Defaults to offshore to preserve
  // the original Valeura behaviour for existing callers.
  offshore?: boolean;
  onClose: () => void;
}

export default function FieldSchematicPanel({ field, plants, offshore = true, onClose }: Props): JSX.Element {
  const [activeId, setActiveId] = useState<number | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const totalCmls = plants.reduce((s, p) => s + (p.cml_count ?? 0), 0);
  const totalAssets = plants.reduce((s, p) => s + (p.asset_count ?? 0), 0);

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center p-6"
      style={{ background: "rgba(6,18,31,0.55)", backdropFilter: "blur(2px)" }}
      onClick={onClose}
      role="dialog" aria-modal="true"
      aria-label={`${offshore ? "Field" : "Site"} — ${field}`}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.18 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden"
        style={{ background: "var(--paper-2)", border: "1px solid var(--hairline)", borderRadius: 12 }}
      >
        {/* ── Header (light, matches the Atlas theme) ── */}
        <div className="shrink-0 px-6 py-3 flex items-center justify-between"
             style={{ background: "var(--paper-2)", color: "var(--ink-1)",
                      borderBottom: "1px solid var(--hairline)" }}>
          <div className="min-w-0">
            <div className="font-mono uppercase flex items-center gap-2"
                 style={{ fontSize: 10, letterSpacing: ".22em", color: "var(--accent-deep)" }}>
              {offshore ? <><Anchor size={11} /> FIELD</> : <><Factory size={11} /> SITE</>}
            </div>
            <div className="font-display font-bold truncate" style={{ fontSize: 18, letterSpacing: "-.01em" }}>
              {field || "Field"}
            </div>
          </div>
          <div className="flex items-center gap-5">
            <Kpi label={offshore ? "PLATFORMS" : "UNITS"} value={plants.length} />
            <Kpi label="CMLS" value={totalCmls} />
            <Kpi label="ASSETS" value={totalAssets} />
            <button onClick={onClose} aria-label="Close"
                    className="ml-2 p-1.5 rounded hover:bg-black/5 transition-colors"
                    style={{ color: "var(--ink-3)" }}>
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ── Body: plant cards (atlas-cards-scroll reuses the Atlas rail card
             styling — rounded, health tint + left accent, hover lift) ── */}
        <div className="flex-1 min-h-0 overflow-y-auto p-3 atlas-cards-scroll" style={{ background: "var(--paper)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 8 }}>
            {plants.map((p, i) => (
              <PlantCard key={p.id} plant={p} isActive={activeId === p.id}
                         animDelay={Math.min(i, 8) * 0.03}
                         onMouseEnter={() => setActiveId(p.id)}
                         onMouseLeave={() => setActiveId(null)} />
            ))}
          </div>
        </div>

        {/* ── Honesty caption ── */}
        <div className="shrink-0 px-6 py-2 font-mono"
             style={{ fontSize: 9.5, letterSpacing: ".06em", color: "var(--ink-4)",
                      background: "var(--paper-2)", borderTop: "1px solid var(--hairline)" }}>
          {offshore ? "Platforms" : "Units"} share one published {offshore ? "field" : "site"} coordinate.
          Counts are live, tenant-scoped.
        </div>
      </motion.div>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div className="text-right">
      <div className="font-mono uppercase" style={{ fontSize: 8.5, letterSpacing: ".18em", opacity: 0.6 }}>{label}</div>
      <div className="font-display font-bold tabular-nums leading-none" style={{ fontSize: 16 }}>{value.toLocaleString()}</div>
    </div>
  );
}
